import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronRight, Info } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Cell, LabelList,
  ResponsiveContainer, Tooltip,
} from 'recharts';
import { FullScreenChartModal, ExpandButton } from '../FullScreenChartModal/FullScreenChartModal';
import { resolveCityDataKey } from '../../services/companyFileReader';
import { getChartLabels, normalizeChartKey } from '../../utils/chartLabels';
import styles from './SideChartPanel.module.css';

import { scoreAccent, scoreBarFill } from '../../utils/scoreColors';
import { SCORE_SCALE_BAD, SCORE_SCALE_MID, SCORE_SCALE_GOOD } from '../../../../constants/scoreScale';

// ── Brand palette ─────────────────────────────────────────────────────────────
const VIOLET     = '#8C74FF';
const C_TEAL     = '#273308';
const C_LINE     = '#E5E5CC';
const C_TRACK    = '#eaeadc';

/** Team-lead preview: axis names beside/below the plot. Set false for captions under the card. */
const SHOW_AXIS_LABELS_ON_CHART = false;

/** Only these charts show the X-axis name under the plot. */
const X_AXIS_LABEL_CHARTS = new Set(['puntualita', 'passeggeri_per_linea']);
const scoreGradient = (v) => scoreAccent(v);
const scoreBarColor = (v) => scoreBarFill(v);

const TOOLTIP_BOX = {
  background: '#1a1e0e',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  fontSize: 12,
  color: '#fff',
  fontFamily: 'Inter, sans-serif',
  padding: '8px 10px',
};

function chartValueColor(name, value, mode, maxVal = 10) {
  if (mode === 'dist') return distLabelColor(String(name ?? ''), value);
  if (mode === 'score') return scoreBarColor(value);
  return scoreBarColor(Math.min(Number(value) / maxVal, 1) * 10);
}

function ScoreTooltip({ active, payload, label, mode, maxVal }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={TOOLTIP_BOX}>
      {label ? <div style={{ marginBottom: 4, color: 'rgba(255,255,255,0.7)' }}>{label}</div> : null}
      {payload.map((item) => {
        const value = Number(item.value);
        if (!Number.isFinite(value)) return null;
        const color = chartValueColor(label || item.name, value, mode, maxVal);
        const text = mode === 'dist' ? `${Math.round(value * 100)}%` : value.toFixed(2);
        return (
          <div key={item.dataKey || item.name} style={{ color, fontWeight: 700 }}>
            {item.name && item.name !== label ? `${item.name}: ` : ''}{text}
          </div>
        );
      })}
    </div>
  );
}
const distLabelColor = (label, value) => {
  const l = label.toLowerCase();
  const isPos = /perfett|eccell|ottim|ben\s|pulito|assenz|nessun|complet|inequivoc|ideal|esaust|presenti\se|rampa\se|percorsi\se|funzionant|^si$|si,/.test(l);
  const isNeg = /comprom|trascur|scarsi|inadeg|forte|elevato|insuff|non\svisib|non\sprot|assenza\sdi|illegg|sconfort|usurato|assenti\so|^no$/.test(l);
  const isNeu = /discret|parzial|suffic|abbastanz|buona|adeg|mantenut|neutri|moderat/.test(l);
  if (isNeg) return SCORE_SCALE_BAD;
  if (isNeu) return SCORE_SCALE_MID;
  if (isPos) return SCORE_SCALE_GOOD;
  return scoreGradient(value * 10);
};

function ChartTitleRow({ title, labels }) {
  const displayTitle = labels?.nome || title;
  const descrizione = labels?.descrizione || '';
  if (!displayTitle && !descrizione) return null;
  return (
    <div className={styles.titleRow}>
      {displayTitle ? <div className={styles.chartTitle}>{displayTitle}</div> : <div className={styles.chartTitleSpacer} />}
      {descrizione ? (
        <button
          type="button"
          className={styles.infoBtn}
          aria-label="Mostra descrizione grafico"
          title="Descrizione"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Info size={15} strokeWidth={2.4} />
          <span className={styles.infoTip} role="tooltip">{descrizione}</span>
        </button>
      ) : null}
    </div>
  );
}

function shouldShowXAxisCaption(labels, title) {
  return [labels?.nome, title]
    .filter(Boolean)
    .some((value) => X_AXIS_LABEL_CHARTS.has(normalizeChartKey(value)));
}

function ChartAxisYCaption({ labels }) {
  if (SHOW_AXIS_LABELS_ON_CHART || !labels?.asseY) return null;
  return (
    <div className={`${styles.axisCaption} ${styles.axisCaptionAbove}`}>
      <span className={styles.axisYValue}>{labels.asseY}</span>
    </div>
  );
}

function ChartAxisXCaption({ labels, title }) {
  if (SHOW_AXIS_LABELS_ON_CHART || !labels?.asseX) return null;
  if (!shouldShowXAxisCaption(labels, title)) return null;
  return (
    <div className={`${styles.axisCaption} ${styles.axisCaptionBelow}`}>
      <span className={styles.axisXValue}>{labels.asseX}</span>
    </div>
  );
}

/** Split a long axis title into up to 2 lines at a word boundary. */
function splitAxisLabel(text, maxLineLen = 20) {
  const t = String(text || '').trim().replace(/\s+/g, ' ');
  if (!t) return [];
  if (t.length <= maxLineLen) return [t];

  const words = t.split(' ');
  if (words.length === 1) {
    const mid = Math.ceil(t.length / 2);
    return [t.slice(0, mid), t.slice(mid)];
  }

  const target = Math.ceil(t.length / 2);
  let best = 1;
  let bestScore = Infinity;
  for (let i = 1; i < words.length; i += 1) {
    const left = words.slice(0, i).join(' ');
    const score = Math.abs(left.length - target) + (left.length > maxLineLen * 1.4 ? 20 : 0);
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return [words.slice(0, best).join(' '), words.slice(best).join(' ')].filter(Boolean);
}

/** Axis names in reserved layout slots — never drawn inside the plot / over bars. */
function ChartPlotWithAxisNames({ labels, chartHeight, children }) {
  const show = SHOW_AXIS_LABELS_ON_CHART;
  const asseX = show ? labels?.asseX : '';
  const asseY = show ? labels?.asseY : '';
  const yLines = splitAxisLabel(asseY);
  if (!asseX && !yLines.length) {
    return <ResponsiveContainer width="100%" height={chartHeight}>{children}</ResponsiveContainer>;
  }
  return (
    <div className={yLines.length > 1 ? styles.plotWithAxesWide : styles.plotWithAxes}>
      {yLines.length ? (
        <div className={styles.yAxisName} title={asseY}>
          <div className={styles.yAxisNameInner}>
            {yLines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </div>
        </div>
      ) : (
        <div className={styles.yAxisNameSpacer} />
      )}
      <div className={styles.plotMain}>
        <ResponsiveContainer width="100%" height={chartHeight}>{children}</ResponsiveContainer>
        {asseX ? <div className={styles.xAxisName} title={asseX}>{asseX}</div> : null}
      </div>
    </div>
  );
}

/** Tick band only — axis titles live in HTML slots outside the SVG. */
function categoryTickSpace(rotate, maxLabelLen) {
  const tickMargin = rotate ? 12 : 6;
  const bottom = rotate ? Math.min(maxLabelLen * 5.5 + 16, 120) : 28;
  return { tickMargin, bottom };
}

function axisPlotMargins({ ticks, leftPad, isFullscreen }) {
  const bottomPad = isFullscreen ? Math.max(36, ticks.bottom * 0.5) : ticks.bottom;
  return {
    margin: {
      top: 28, // room for bar value labels above bars
      right: 10,
      bottom: bottomPad,
      left: leftPad,
    },
    chartBottom: bottomPad,
  };
}

// ── Type detectors ────────────────────────────────────────────────────────────
const isNumericMap = (obj) => {
  if (typeof obj !== 'object' || obj === null) return false;
  const vals = Object.values(obj);
  return vals.length > 0 && vals.every((v) => typeof v === 'number' && isFinite(v));
};
const isDistribution = (data) => {
  const vals = Object.values(data).filter((v) => typeof v === 'number' && isFinite(v));
  if (!vals.length) return false;
  // If every value is between 0 and 1 (inclusive), treat as percentage distribution.
  // This covers both proper distributions (sum ≈ 1) and per-item binary/fractional scores
  // such as those found in sito_web and app charts (values 0 or 1).
  return vals.every((v) => v >= 0 && v <= 1);
};
const isGroupedSeries = (obj) => {
  if (typeof obj !== 'object' || obj === null) return false;
  const entries = Object.entries(obj);
  if (entries.length < 2) return false;
  const innerSets = [];
  for (const [, v] of entries) {
    if (typeof v !== 'object' || v === null) return false;
    const inner = Object.entries(v);
    if (!inner.length || !inner.every(([, iv]) => typeof iv === 'number' && isFinite(iv))) return false;
    innerSets.push(new Set(Object.keys(v)));
  }
  const firstKeys = innerSets[0];
  return innerSets.every((s) => s.size === firstKeys.size && Array.from(firstKeys).every((k) => s.has(k)));
};

// ── Chart: Grouped histogram ──────────────────────────────────────────────────
function IstogrammaGruppato({ title, data, onZoom, labels }) {
  const categories = Object.keys(data);
  const seriesKeys = Object.keys(data[categories[0]]);
  const allValues = categories.flatMap((cat) => Object.values(data[cat]));
  const isDist = allValues.every((v) => v >= 0 && v <= 1);
  const mode = isDist ? 'dist' : (Math.max(...allValues, 0) <= 10 ? 'score' : 'custom');
  const yMax = isDist ? 1 : Math.max(...allValues, 10);
  const chartData = categories.map((cat) => ({ category: cat, ...data[cat] }));
  const maxLabelLen = Math.max(...categories.map((s) => s.length));
  const rotate = categories.length > 2 || maxLabelLen > 11;
  const ticks = categoryTickSpace(rotate, maxLabelLen);
  const leftPad = isDist ? 36 : 26;
  const { margin, chartBottom } = axisPlotMargins({ ticks, leftPad, isFullscreen: false });
  const chartH = 180 + chartBottom;
  const yDomain = yMax;
  const fmtVal = (v) => v === 0 ? '' : isDist ? `${Math.round(v * 100)}%` : v.toFixed(1);

  return (
    <div className={styles.chartCard} style={{ position: 'relative' }}>
      <ExpandButton onClick={onZoom} />
      <ChartTitleRow title={title} labels={labels} />
      <div className={styles.seriesLegend}>
        {seriesKeys.map((key, i) => (
          <div key={key} className={styles.seriesItem}>
            <span className={styles.seriesLabel}>{i + 1}. {key}</span>
          </div>
        ))}
      </div>
      <ChartAxisYCaption labels={labels} />
      <ChartPlotWithAxisNames labels={labels} chartHeight={chartH}>
        <BarChart data={chartData} margin={margin}
          barCategoryGap="22%" barGap={4}>
          <XAxis dataKey="category"
            tick={{ fontSize: 11, fill: '#5a5c50', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}
            axisLine={false} tickLine={false} interval={0}
            angle={rotate ? -42 : 0} textAnchor={rotate ? 'end' : 'middle'}
            tickMargin={ticks.tickMargin}
          />
          <YAxis domain={[0, yDomain]}
            tickFormatter={isDist ? (v) => `${Math.round(v * 100)}%` : undefined}
            tick={{ fontSize: 10, fill: '#aaa', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
            axisLine={false} tickLine={false} width={isDist ? 36 : 26} tickCount={5}
          />
          <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }}
            content={<ScoreTooltip mode={mode} maxVal={yMax} />} />
          {seriesKeys.map((key) => (
            <Bar key={key} dataKey={key} radius={[5, 5, 0, 0]} maxBarSize={44}>
              {chartData.map((row, rowIndex) => (
                <Cell key={`${key}-${rowIndex}`} fill={chartValueColor(row.category, row[key], mode, yMax)} />
              ))}
              <LabelList dataKey={key} position="top" formatter={fmtVal}
                style={{ fontSize: 10, fontWeight: 600, fill: C_TEAL, fontFamily: 'Inter, sans-serif' }} />
            </Bar>
          ))}
        </BarChart>
      </ChartPlotWithAxisNames>
      <ChartAxisXCaption labels={labels} title={title} />
    </div>
  );
}

// ── Chart: Single histogram ───────────────────────────────────────────────────
function Istogramma({ title, entries, mode, heightOverride, labels }) {
  const maxVal = mode === 'dist' ? 1 : mode === 'score' ? 10 : Math.max(...entries.map((e) => e.value), 1);
  const maxLabelLen = Math.max(...entries.map((e) => e.name.length));
  const rotate = entries.length > 3 || maxLabelLen > 11;
  const isFullscreen = heightOverride === '100%' || typeof heightOverride === 'number';
  const ticks = categoryTickSpace(rotate, maxLabelLen);
  const leftPad = mode === 'dist' ? 36 : 26;
  const { margin, chartBottom } = axisPlotMargins({ ticks, leftPad, isFullscreen });
  const chartH = heightOverride || (170 + chartBottom);

  const formatLabel = (v) => {
    if (v === 0) return '';
    if (mode === 'dist') return `${Math.round(v * 100)}%`;
    return v >= 10 ? v.toFixed(0) : v.toFixed(1);
  };
  const getColor = (name, value) => {
    if (mode === 'dist') return distLabelColor(name, value);
    if (mode === 'score') return scoreBarColor(value);
    return scoreBarColor(Math.min(value / maxVal, 1) * 10);
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: heightOverride === '100%' ? '100%' : 'auto' }}>
      {title || labels?.descrizione || labels?.nome ? <ChartTitleRow title={title} labels={labels} /> : null}
      <ChartAxisYCaption labels={labels} />
      <ChartPlotWithAxisNames labels={labels} chartHeight={chartH}>
        <BarChart data={entries} margin={margin}
          barCategoryGap="26%">
          <XAxis dataKey="name"
            tick={{ fontSize: 11, fill: '#5a5c50', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}
            axisLine={false} tickLine={false} interval={0}
            angle={rotate ? -42 : 0} textAnchor={rotate ? 'end' : 'middle'}
            tickMargin={ticks.tickMargin}
          />
          <YAxis domain={[0, maxVal]}
            tickFormatter={mode === 'dist' ? (v) => `${Math.round(v * 100)}%` : undefined}
            tick={{ fontSize: 10, fill: '#aaa', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
            axisLine={false} tickLine={false}
            width={mode === 'dist' ? 36 : 26}
            tickCount={5}
          />
          <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }}
            content={<ScoreTooltip mode={mode} maxVal={maxVal} />} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={56}>
            <LabelList dataKey="value" position="top" formatter={formatLabel}
              style={{ fontSize: 11, fontWeight: 600, fill: C_TEAL, fontFamily: 'Inter, sans-serif' }} />
            {entries.map((e, i) => <Cell key={i} fill={getColor(e.name, e.value)} />)}
          </Bar>
        </BarChart>
      </ChartPlotWithAxisNames>
      <ChartAxisXCaption labels={labels} title={title} />
    </div>
  );
}

// ── Recursive chart renderer ──────────────────────────────────────────────────
function renderGraficoNode(name, data, onZoom, ctx = {}) {
  if (data === null || data === undefined || typeof data !== 'object') return null;
  const labels = getChartLabels(ctx.macro, ctx.subKey, name, ctx.parentKey)
    || (ctx.parentKey ? getChartLabels(ctx.macro, ctx.subKey, ctx.parentKey) : null);

  if (isGroupedSeries(data)) {
    return <IstogrammaGruppato title={name} data={data} labels={labels} onZoom={() => onZoom(name, data, labels)} />;
  }

  if (isNumericMap(data)) {
    const entries = Object.entries(data).map(([k, v]) => ({ name: k, value: v }));
    if (!entries.length) return null;
    const isDist = isDistribution(data);
    const maxRaw = Math.max(...entries.map((e) => e.value).filter((v) => v > 0));
    const mode = isDist ? 'dist' : maxRaw <= 10 ? 'score' : 'custom';
    return (
      <div className={styles.chartCard} style={{ position: 'relative' }}>
        <ExpandButton onClick={() => onZoom(name, data, labels)} />
        <Istogramma title={name} entries={entries} mode={mode} labels={labels} />
      </div>
    );
  }

  const childEntries = Object.entries(data);
  if (!childEntries.length) return null;
  const numPairs = childEntries.filter(([, v]) => typeof v === 'number' && isFinite(v));
  const objPairs = childEntries.filter(([, v]) => typeof v === 'object' && v !== null);
  const nodes = [];

  if (numPairs.length > 0) {
    const flat = Object.fromEntries(numPairs);
    const entries = numPairs.map(([k, v]) => ({ name: k, value: v }));
    const isDist = isDistribution(flat);
    const maxRaw = Math.max(...numPairs.map(([, v]) => v).filter((v) => v > 0));
    const mode = isDist ? 'dist' : maxRaw <= 10 ? 'score' : 'custom';
    nodes.push(
      <div key={`${name}-flat`} className={styles.chartCard} style={{ position: 'relative' }}>
        <ExpandButton onClick={() => onZoom(name, flat, labels)} />
        <Istogramma title={name} entries={entries} mode={mode} labels={labels} />
      </div>
    );
  }

  for (const [k, v] of objPairs) {
    const child = renderGraficoNode(k, v, onZoom, { ...ctx, parentKey: name });
    if (child) nodes.push(child);
  }

  if (!nodes.length) return null;
  return <div className={styles.nodeGroup}>{nodes}</div>;
}

// ── Score gauge ───────────────────────────────────────────────────────────────
function ScoreGauge({ score, size = 64 }) {
  if (score === null || score === undefined) return null;
  const r = (size - 8) / 2;
  const cx = size / 2, cy = size / 2;
  const arc = Math.PI * r;
  const filled = Math.max(0, Math.min(1, score / 10)) * arc;
  const col = scoreGradient(score);
  return (
    <div className={styles.gauge} style={{ width: size, height: size / 2 + 12 }}>
      <svg width={size} height={size / 2 + 2}>
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke={C_TRACK} strokeWidth={5} strokeLinecap="round" />
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke={col} strokeWidth={5} strokeLinecap="round"
          strokeDasharray={`${filled} ${arc}`} />
      </svg>
      <div className={styles.gaugeValue} style={{ fontSize: size * 0.24, color: col }}>
        {score.toFixed(1)}
      </div>
    </div>
  );
}



// ── Main panel ────────────────────────────────────────────────────────────────

/**
 * SideChartPanel — slide-in panel showing all charts for a macro-category.
 * Scrolls automatically to the active sub-category.
 */
const SideChartPanel = ({ company, macro, activeSub, selectedCity, onClose }) => {
  const [zoomedChart, setZoomedChart] = useState(null);
  const subsRef = useRef({});

  const cittaObj = company?.citta || company?.['città'] || {};
  const cities = Object.keys(cittaObj);
  const cityName = selectedCity
    ? resolveCityDataKey(cittaObj, selectedCity)
    : resolveCityDataKey(cittaObj, cities[0]) || cities[0] || '';
  const cityData = cittaObj[cityName];
  const catData = cityData?.[macro];
  const catScore = typeof catData?.punteggio_globale === 'number' ? catData.punteggio_globale : null;
  const allSubs = catData?.sub ? Object.entries(catData.sub) : [];
  const grafici = catData?.grafici ?? {};
  const macroLabel = macro.replace(/_/g, ' ').toUpperCase();

  useEffect(() => {
    if (!activeSub) return;
    const el = subsRef.current[activeSub];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeSub]);

  return (
    <>
      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.panelHeader}>
          <div className={styles.panelHeaderLeft}>
            <div className={styles.panelTag}>{macroLabel}</div>
            <h3 className={styles.panelTitle}>GRAFICI & DATI</h3>
            <div className={styles.panelSub}>{allSubs.length} categorie · tutti i grafici dal JSON</div>
          </div>
          <div className={styles.panelHeaderRight}>
            <ScoreGauge score={catScore} size={66} />
            <button onClick={onClose} className={styles.closeBtn}>
              <X size={14} color="#fff" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={styles.panelBody}>
          {allSubs.length === 0 && (
            <div className={styles.emptyState}>NESSUNA MICROCATEGORIA DISPONIBILE</div>
          )}
          {allSubs.map(([subKey, subData]) => {
            const subScore = typeof subData?.punteggio_globale === 'number' ? subData.punteggio_globale : null;
            const graficiGroup = grafici[subKey];
            const isActive = activeSub === subKey;
            const chartItemsObj = graficiGroup
              ? Object.entries(graficiGroup).filter(([, v]) => typeof v === 'object' && v !== null)
              : [];
            const chartItemsNum = graficiGroup
              ? Object.entries(graficiGroup).filter(([, v]) => typeof v === 'number' && isFinite(v))
              : [];
            const chartItems = chartItemsObj;
            const breakdown = subData?.breakdown;
            const hasObjCharts = chartItems.length > 0;
            const hasNumCharts = chartItemsNum.length > 0;
            const hasCharts = hasObjCharts || hasNumCharts;
            const hasBreakdown = !hasCharts && breakdown && Object.keys(breakdown).length > 0;

            return (
              <div key={subKey} ref={(el) => { subsRef.current[subKey] = el; }} className={styles.subSection}>
                <div className={styles.subHeader} style={{ borderBottomColor: isActive ? VIOLET : C_LINE }}>
                  <div className={styles.subHeaderLeft}>
                    {isActive && <ChevronRight size={14} color={VIOLET} />}
                    <span className={styles.subName} style={{ color: isActive ? VIOLET : C_TEAL }}>
                      {subKey.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {subScore !== null && (
                    <span className={styles.subScore} style={{ color: scoreGradient(subScore) }}>
                      {subScore.toFixed(1)}
                    </span>
                  )}
                </div>

                {hasCharts ? (
                  <motion.div className={styles.chartGroup}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.04 }}>
                    {/* Render numeric grafici as a single score bar chart */}
                    {hasNumCharts && (() => {
                      const scoreLabels = getChartLabels(macro, subKey, 'Punteggi');
                      return (
                      <div className={styles.chartCard} style={{ position: 'relative' }}>
                        <ExpandButton onClick={() => setZoomedChart({
                          name: subKey.replace(/_/g, ' '),
                          data: Object.fromEntries(chartItemsNum),
                          labels: scoreLabels,
                        })} />
                        <Istogramma
                          title="Punteggi"
                          entries={chartItemsNum.map(([k, v]) => ({ name: k, value: v }))}
                          mode="score"
                          labels={scoreLabels}
                        />
                      </div>
                      );
                    })()}
                    {/* Render object grafici (distributions, grouped series, etc.) */}
                    {chartItems.map(([name, data]) => {
                      const node = renderGraficoNode(
                        name,
                        data,
                        (n, d, labels) => setZoomedChart({ name: n, data: d, labels }),
                        { macro, subKey },
                      );
                      return node ? <div key={name}>{node}</div> : null;
                    })}
                  </motion.div>
                ) : hasBreakdown ? (() => {
                  const breakdownLabels = getChartLabels(macro, subKey, 'Punteggi parametri');
                  return (
                  <div className={styles.chartCard} style={{ position: 'relative' }}>
                    <ExpandButton onClick={() => setZoomedChart({
                      name: 'Punteggi parametri',
                      data: breakdown,
                      labels: breakdownLabels,
                    })} />
                    <Istogramma
                      title="Punteggi parametri"
                      entries={Object.entries(breakdown).map(([k, v]) => ({ name: k, value: v }))}
                      mode="score"
                      labels={breakdownLabels}
                    />
                  </div>
                  );
                })() : (
                  <div className={styles.noData}>Nessun dato grafico</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {zoomedChart && (
          <FullScreenChartModal 
            isOpen={true} 
            title={zoomedChart.labels?.nome || zoomedChart.name} 
            onClose={() => setZoomedChart(null)}
          >
            {zoomedChart.labels?.descrizione ? (
              <div className={styles.modalDescription}>{zoomedChart.labels.descrizione}</div>
            ) : null}
            {isNumericMap(zoomedChart.data) ? (() => {
              const entries = Object.entries(zoomedChart.data).map(([k, v]) => ({ name: k, value: v }));
              const isDist = isDistribution(zoomedChart.data);
              const maxRaw = Math.max(...entries.map((e) => e.value).filter((v) => v > 0));
              const mode = isDist ? 'dist' : maxRaw <= 10 ? 'score' : 'custom';
              return (
                <div style={{ width: '100%', height: '100%', paddingBottom: '2rem' }}>
                  <Istogramma title="" entries={entries} mode={mode} heightOverride="100%" labels={zoomedChart.labels} />
                </div>
              );
            })() : (
              <div className={styles.modalCharts} style={{ display: 'flex', flexDirection: 'column', gap: '3rem', height: '100%', overflowY: 'auto', paddingBottom: '2rem' }}>
                {Object.entries(zoomedChart.data ?? {}).map(([k, v]) =>
                  isNumericMap(v) ? (() => {
                    const entries = Object.entries(v).map(([n, val]) => ({ name: n, value: val }));
                    const isDist = isDistribution(v);
                    const maxRaw = Math.max(...entries.map((e) => e.value).filter((x) => x > 0));
                    const mode = isDist ? 'dist' : maxRaw <= 10 ? 'score' : 'custom';
                    const childLabels = getChartLabels(macro, activeSub, k, zoomedChart.name) || zoomedChart.labels;
                    return (
                      <div key={k} style={{ height: '70vh', minHeight: '400px' }}>
                        <Istogramma title={k} entries={entries} mode={mode} heightOverride="100%" labels={childLabels} />
                      </div>
                    );
                  })() : null
                )}
              </div>
            )}
          </FullScreenChartModal>
        )}
      </AnimatePresence>
    </>
  );
};

export default SideChartPanel;
